import { Button } from "@majitap/ui";
import { Alert } from "@majitap/ui/arc/alert/alert";
import { Checkbox } from "@majitap/ui/arc/checkbox/checkbox";
import { Dialog, DialogContent } from "@majitap/ui/arc/dialog/dialog";
import { FileDropzone } from "@majitap/ui/arc/file-dropzone/file-dropzone";
import { Input } from "@majitap/ui/arc/input/input";
import { Select } from "@majitap/ui/arc/select/select";
import { Textarea } from "@majitap/ui/arc/textarea/textarea";
import { useMutation, useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { errorOf } from "../../api";
import { fleetQueries } from "../../dispatch";
import { messageFor } from "../../error-copy";
import { uploadShiftPhoto } from "../../files";
import { fleetActions, type OpenShiftBody, type ShiftOptions, shiftOptionsQuery, useRefreshFleet } from "../../fleet";
import styles from "./fleet.module.css";

/** Shift photos, as the API takes them. */
const MAX_PHOTO = 10 * 1024 * 1024;

type Item = ShiftOptions["items"][number];
/** One answer being filled in: a check, a reading, words, or a photo to upload. */
type Draft = { bool?: boolean; text?: string; file?: File | null };

const NO_ASSISTANT = "none";

/** What the API says about each refused item (CHECKLIST_INCOMPLETE), in words. */
const ITEM_PROBLEM: Record<string, string> = {
  missing: "Required.",
  must_be_yes: "Must be yes to start the shift.",
  wrong_kind: "Not the kind of answer this item takes.",
};

/**
 * Open a shift for a driver who can't in the driver app (P8-59): the truck, the assistant and the
 * company's shift checklist, answered on their behalf with the app's rules. Without `driver`, the
 * driver is chosen first: approved drivers not on shift.
 */
export function OpenShiftDialog({ driver, onClose }: { driver?: { id: string; name: string }; onClose: () => void }) {
  const refresh = useRefreshFleet();
  const [driverId, setDriverId] = useState(driver?.id ?? "");
  const drivers = useQuery({ ...fleetQueries.drivers, enabled: !driver });
  const onShift = useQuery({ ...fleetQueries.shifts, enabled: !driver });
  const busy = new Set((onShift.data ?? []).map((s) => s.driver.id));
  const free = (drivers.data ?? []).filter((d) => d.status === "active" && !busy.has(d.id));
  const name = driver?.name ?? drivers.data?.find((d) => d.id === driverId)?.user.name;

  const options = useQuery({ ...shiftOptionsQuery(driverId), enabled: Boolean(driverId) });
  const [truckId, setTruckId] = useState("");
  const [assistantId, setAssistantId] = useState(NO_ASSISTANT);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [problems, setProblems] = useState<Record<string, string>>({});
  const items = options.data?.items ?? [];
  const draftOf = (i: Item): Draft => drafts[i.id] ?? (i.kind === "yes_no" ? { bool: false } : {});
  /** A changed field drops its error; the rest stay until the next submit. */
  const fixed = (key: string) =>
    setProblems((all) => {
      const { [key]: _, ...rest } = all;
      return rest;
    });
  const set = (id: string, d: Draft) => {
    setDrafts((all) => ({ ...all, [id]: d }));
    fixed(id);
  };

  const pickDriver = (id: string) => {
    setDriverId(id);
    setTruckId("");
    setAssistantId(NO_ASSISTANT);
    setDrafts({});
    setProblems({});
  };

  const mutation = useMutation({
    mutationFn: async () => {
      // photos go up first; the answers point at them
      const answers: OpenShiftBody["answers"] = [];
      for (const i of items) {
        const d = draftOf(i);
        if (i.kind === "yes_no") answers.push({ item_id: i.id, bool_value: d.bool === true });
        // number-ok: an odometer or a level, not money; the API takes a JSON number and keeps 2 places
        else if (i.kind === "number" && d.text?.trim()) answers.push({ item_id: i.id, number_value: Number(d.text) });
        else if (i.kind === "text" && d.text?.trim()) answers.push({ item_id: i.id, text_value: d.text.trim() });
        else if (i.kind === "photo" && d.file)
          answers.push({ item_id: i.id, file_id: (await uploadShiftPhoto(d.file)).id });
      }
      return fleetActions.openShift(driverId, {
        truck_id: truckId,
        assistant_id: assistantId === NO_ASSISTANT ? undefined : assistantId,
        answers,
      });
    },
    onSuccess: async () => {
      await refresh();
      onClose();
    },
    onError: (error) => {
      const e = errorOf(error);
      const refused =
        e.code === "CHECKLIST_INCOMPLETE" ? (e.details?.items as Record<string, string> | undefined) : null;
      if (refused) {
        setProblems(Object.fromEntries(Object.entries(refused).map(([id, why]) => [id, ITEM_PROBLEM[why] ?? why])));
      }
    },
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found: Record<string, string> = {};
    if (!truckId) found.truck = "Choose the truck.";
    if (options.data?.requires_assistant && assistantId === NO_ASSISTANT) found.assistant = "Choose the assistant.";
    for (const i of items) {
      const d = draftOf(i);
      // number-ok: only checks the reading parses, as it's sent above
      if (i.kind === "number" && d.text?.trim() && !Number.isFinite(Number(d.text))) found[i.id] = "A number.";
      if (!i.required) continue;
      if (i.kind === "yes_no" && d.bool !== true) found[i.id] = ITEM_PROBLEM.must_be_yes;
      if ((i.kind === "number" || i.kind === "text") && !d.text?.trim()) found[i.id] = "Required.";
      if (i.kind === "photo" && !d.file) found[i.id] = "Add the photo.";
    }
    setProblems(found);
    if (!Object.keys(found).length) mutation.mutate();
  };

  const optionalLabel = (i: Item) => (i.required ? i.label : `${i.label} (optional)`);
  const failed = mutation.error ? errorOf(mutation.error) : null;

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent
        title={name ? `Open a shift for ${name}` : "Open a shift"}
        description="For a driver who can't start it in the driver app. Answer the shift checklist as they would; the shift shows you opened it."
      >
        <form onSubmit={submit} className={styles.form}>
          {driver ? null : (
            <Select
              label="Driver"
              placeholder={drivers.status === "pending" ? "Loading drivers…" : "Choose a driver"}
              value={driverId}
              onValueChange={pickDriver}
              options={free.map((d) => ({ value: d.id, label: d.user.name }))}
              description={
                drivers.status === "success" && free.length === 0
                  ? "Every approved driver is on shift already."
                  : "Approved drivers who aren't on shift."
              }
            />
          )}
          {driverId && options.status === "pending" ? (
            <p className={styles.muted}>Loading the shift checklist…</p>
          ) : null}
          {options.error ? (
            <Alert tone="danger" title="Can't open a shift for this driver">
              {messageFor(errorOf(options.error))}
            </Alert>
          ) : null}
          {options.data ? (
            <>
              <div className={styles.pair}>
                <div className={styles.field}>
                  <Select
                    label="Truck"
                    placeholder={options.data.trucks.length ? "Choose a truck" : "No truck free"}
                    value={truckId}
                    onValueChange={(v) => {
                      setTruckId(v);
                      fixed("truck");
                    }}
                    options={options.data.trucks.map((t) => ({ value: t.id, label: t.plate_number }))}
                  />
                  {problems.truck ? <span className={styles.fieldError}>{problems.truck}</span> : null}
                </div>
                <div className={styles.field}>
                  <Select
                    label={options.data.requires_assistant ? "Assistant" : "Assistant (optional)"}
                    value={assistantId}
                    onValueChange={(v) => {
                      setAssistantId(v);
                      fixed("assistant");
                    }}
                    options={[
                      { value: NO_ASSISTANT, label: "No assistant" },
                      ...options.data.assistants.map((a) => ({ value: a.id, label: a.name })),
                    ]}
                  />
                  {problems.assistant ? <span className={styles.fieldError}>{problems.assistant}</span> : null}
                </div>
              </div>
              {options.data.trucks.length === 0 ? (
                <p className={styles.muted}>
                  Every active truck of this driver's fleet is on another shift, or none is active.
                </p>
              ) : null}
              {items.map((i) => {
                const d = draftOf(i);
                if (i.kind === "yes_no")
                  return (
                    <div key={i.id} className={styles.field}>
                      <Checkbox
                        label={optionalLabel(i)}
                        description={i.help_text ?? undefined}
                        checked={d.bool === true}
                        onCheckedChange={(v) => set(i.id, { bool: v === true })}
                      />
                      {problems[i.id] ? <span className={styles.fieldError}>{problems[i.id]}</span> : null}
                    </div>
                  );
                if (i.kind === "number")
                  return (
                    <Input
                      key={i.id}
                      label={optionalLabel(i)}
                      inputMode="decimal"
                      description={i.help_text ?? undefined}
                      value={d.text ?? ""}
                      onChange={(e) => set(i.id, { text: e.target.value })}
                      error={problems[i.id]}
                    />
                  );
                if (i.kind === "text")
                  return (
                    <Textarea
                      key={i.id}
                      label={optionalLabel(i)}
                      rows={2}
                      description={i.help_text ?? undefined}
                      value={d.text ?? ""}
                      onChange={(e) => set(i.id, { text: e.target.value })}
                      error={problems[i.id]}
                    />
                  );
                return (
                  <div key={i.id} className={styles.fileField}>
                    <FileDropzone
                      label={optionalLabel(i)}
                      description={i.help_text ?? "Drop a photo here or choose one"}
                      note="An image up to 10 MB"
                      accept="image/*"
                      multiple={false}
                      maxFiles={1}
                      compactAt={1}
                      maxSize={MAX_PHOTO}
                      onFilesChange={(files) => set(i.id, { file: files.find((f) => f.size <= MAX_PHOTO) ?? null })}
                    />
                    {problems[i.id] ? <span className={styles.fieldError}>{problems[i.id]}</span> : null}
                  </div>
                );
              })}
            </>
          ) : null}
          {failed ? (
            <Alert tone="danger" title="The shift didn't open">
              {failed.code === "CHECKLIST_INCOMPLETE" ? "Some checklist answers need attention." : messageFor(failed)}
            </Alert>
          ) : null}
          <div className={styles.footer}>
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" loading={mutation.isPending} disabled={!options.data}>
              Open shift
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
